import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import NavButtons from '../components/NavButtons';
import StepHeading from '../components/StepHeading';
import OpportunityBanner from '../components/OpportunityBanner';
import { BD } from '../bookDemoTheme';
import { PLACEHOLDER_SAVINGS_BY_REVENUE, REVENUE_RANGES } from '../bookDemoConstants';
import { RevenueRange } from '../types';

interface RevenueStepProps {
  value: RevenueRange | null;
  onChange: (value: RevenueRange) => void;
  onBack: () => void;
  onContinue: () => void;
}

/**
 * Desktop height budget. Everything else in the step — header, progress bar, heading and the nav
 * buttons — costs ~520px, and the banner slot costs another ~130px when it is open. The list takes
 * exactly what is left, so it grows with the viewport instead of stranding the buttons below a gap
 * (the nav is `mt-auto` inside a flex-1 main, so any height the list does not claim shows up as
 * empty space above it). Neither height depends on how many ranges exist — re-cutting the ranges
 * changes only how far the list scrolls internally.
 *
 * min-h keeps the list usable if the window is shorter than the chrome; that case scrolls the page,
 * which beats collapsing the list to nothing.
 */
const LIST_HEIGHT = 'lg:h-[calc(100vh-520px)] lg:min-h-[140px]';
const LIST_HEIGHT_WITH_BANNER = 'lg:h-[calc(100vh-650px)] lg:min-h-[140px]';
/** Matches the Tailwind duration below; used to scroll after the resize has settled. */
const TRANSITION_MS = 300;

const RevenueStep: React.FC<RevenueStepProps> = ({ value, onChange, onBack, onContinue }) => {
  const { t } = useTranslation();
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  // `lt1m` has no savings figure, so picking it shows no banner and the list keeps full height.
  const savings = value ? PLACEHOLDER_SAVINGS_BY_REVENUE[value] : null;

  useEffect(() => {
    if (!value) return;
    // Wait out the height transition — scrolling first would aim at the pre-resize layout.
    const timer = setTimeout(
      () => selectedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
      TRANSITION_MS
    );
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="flex flex-col flex-1">
      <StepHeading title={t('bookDemo.revenue.title')} subtitle={t('bookDemo.revenue.subtitle')} />

      <div className="max-w-[636px] mx-auto w-full flex flex-col">
        {/* Always mounted so the reveal can animate; collapsed to zero height until there is a figure. */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            savings ? 'max-h-[240px] opacity-100 mb-2.5 sm:mb-4' : 'max-h-0 opacity-0 mb-0'
          }`}
          aria-hidden={!savings}
        >
          {savings && <OpportunityBanner amount={savings} compact />}
        </div>

        <div
          className={`space-y-2.5 sm:space-y-4 lg:overflow-y-auto lg:pr-1 transition-all duration-300 ${
            savings ? LIST_HEIGHT_WITH_BANNER : LIST_HEIGHT
          }`}
        >
          {REVENUE_RANGES.map(option => {
            const isSelected = value === option.id;
            return (
              <button
                key={option.id}
                ref={isSelected ? selectedRef : undefined}
                type="button"
                onClick={() => onChange(option.id)}
                aria-pressed={isSelected}
                className={`w-full rounded-xl border p-4 sm:px-5 sm:py-4 flex items-center justify-between text-left transition duration-150 ${
                  BD.cardBg
                } ${BD.cardShadow} ${
                  isSelected ? `${BD.selectedBorder} ${BD.selectedBg}` : `border-transparent ${BD.hoverBorder}`
                }`}
              >
                <div>
                  <p className={`text-[16px] sm:text-[20px] leading-5 font-semibold ${BD.textPrimary}`}>
                    {t(option.labelKey)}
                  </p>
                  <p className={`text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium mt-2 sm:mt-3 ${BD.textMuted}`}>
                    {t(`bookDemo.revenue.descriptions.${option.id}`)}
                  </p>
                </div>
                <span
                  className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 ${
                    isSelected ? `${BD.accentBgDeep} border-transparent` : BD.borderStrong
                  }`}
                >
                  {isSelected && <span className={`w-3 h-3 rounded-full ${BD.stopDot}`} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <NavButtons onBack={onBack} onContinue={onContinue} continueDisabled={!value} />
    </div>
  );
};

export default RevenueStep;
